export interface FormationInscriptionData {
  // Informations personnelles
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  pays?: string;
  
  // Informations entreprise
  entreprise: string;
  siren: string;
  poste: string;
  posteAutre?: string;
  secteurActivite: string;
  secteurAutre?: string;
  tailleEntreprise: 'startup' | 'tpe' | 'pme' | 'eti' | 'grande-entreprise';
  
  // Formation choisie
  formationId: string;
  formationName: string;
  formationPrice: string;
  personCount?: number;
  // Besoins spécifiques
  niveauExperience: 'debutant' | 'intermediaire' | 'avance';
  objectifsPrincipaux: string;
  questionsSpecifiques?: string;
  
  // Marketing
  commentConnaissance: 'site-web' | 'reseaux-sociaux' | 'bouche-oreille' | 'partenaire' | 'autre';
  
  // RGPD
  consentementRGPD: boolean;
  consentementNewsletter: boolean;
}

export class FormationInscriptionForm {
  private formData: FormationInscriptionData;
  private element: HTMLElement;
  private submitCallback?: (data: FormationInscriptionData) => Promise<void>;

  constructor(initialData?: Partial<FormationInscriptionData>) {
    this.formData = {
      nom: '',
      prenom: '',
      email: '',
      adresse: '',
      codePostal: '',
      ville: '',
      pays: '',
      entreprise: '',
      siren: '',
      poste: '',
      posteAutre: '',
      secteurActivite: '',
      secteurAutre: '',
      tailleEntreprise: 'pme',
      formationId: initialData?.formationId || '',
      formationName: initialData?.formationName || '',
      formationPrice: initialData?.formationPrice || '',
      personCount: initialData?.personCount || 1,
      niveauExperience: 'debutant',
      objectifsPrincipaux: '',
      commentConnaissance: 'site-web',
      consentementRGPD: false,
      consentementNewsletter: false,
      ...initialData
    };
    
    this.element = this.createFormElement();
    this.setupEventListeners();
  }

  private createFormElement(): HTMLElement {
    const form = document.createElement('form');
    form.className = 'formation-inscription-form max-w-4xl mx-auto p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl text-white shadow-xl';
    form.innerHTML = `
      <!-- En-tête avec bouton de fermeture -->
      <div class="flex justify-between items-start mb-8">
        <div>
          <h2 class="text-2xl font-bold text-white mb-2">
            Inscription à la formation : ${this.formData.formationName}
          </h2>
          <p class="text-white/80">Tarif : ${this.formData.formationPrice}</p>
        </div>
        <button type="button" class="btn-close ml-4 p-2 text-white/70 hover:text-white transition-colors flex-shrink-0 rounded-full hover:bg-white/10" title="Fermer">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Sélecteur de participants -->
      <div class="mb-8">
        <label class="block text-sm font-medium text-white mb-2">Nombre de participants</label>
        <div class="inline-flex items-center gap-3 select-none">
          <button type="button" id="personMinus" class="stepper-btn w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition flex items-center justify-center" aria-label="Diminuer">
            <span class="text-lg leading-none" aria-hidden="true">−</span>
          </button>
          <input type="number" id="personCountInput" name="personCount" min="1" value="${this.formData.personCount || 1}"
                 class="stepper-input text-center px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-white focus:outline-none focus-border-accent"
                 style="width:72px; flex:0 0 auto;" />
          <button type="button" id="personPlus" class="stepper-btn w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition flex items-center justify-center" aria-label="Augmenter">
            <span class="text-lg leading-none" aria-hidden="true">+</span>
          </button>
        </div>
        <p class="text-white/80 text-sm mt-2">Total estimé: <span id="personTotal" class="font-semibold"></span></p>
      </div>

      <!-- Informations personnelles -->
      <div class="grid md:grid-cols-2 gap-6 mb-8">
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-white border-b border-white/20 pb-2">
            Informations personnelles
          </h3>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-white/90 mb-2">Prénom</label>
              <input type="text" name="prenom" required 
                    class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent">
            </div>
            <div>
              <label class="block text-sm font-medium text-white/90 mb-2">Nom</label>
              <input type="text" name="nom" required 
                     class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Email professionnel</label>
            <input type="email" name="email" required 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Téléphone</label>
            <input type="tel" name="telephone" 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="06 12 34 56 78">
          </div>

          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Adresse</label>
            <input type="text" name="adresse"
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="Adresse postale (numéro et rue, bâtiment, etc.)">
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-white/90 mb-2">Code postal</label>
              <input type="text" name="codePostal"
                     class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="75001">
            </div>
            <div>
              <label class="block text-sm font-medium text-white/90 mb-2">Ville</label>
              <input type="text" name="ville"
                     class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="Paris">
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Pays</label>
            <input type="text" name="pays"
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="France">
          </div>
        </div>

        <!-- Informations entreprise -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-white border-b border-white/20 pb-2">
            Informations entreprise
          </h3>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Entreprise</label>
            <input type="text" name="entreprise" required 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="Raison sociale">
          </div>

          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">SIREN</label>
            <input type="text" name="siren" pattern="^\\d{9}$" required inputmode="numeric" 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent" placeholder="9 chiffres (ex: 123456789)" title="9 chiffres sans espace (obligatoire)">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Poste/Fonction</label>
            <select name="poste" required 
                    class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus-border-accent">
              <option value="">Sélectionnez votre poste</option>
              <option value="dirigeant">Dirigeant / CEO</option>
              <option value="directeur">Directeur / Manager</option>
              <option value="chef-projet">Chef de projet</option>
              <option value="responsable-marketing">Responsable marketing</option>
              <option value="responsable-commercial">Responsable commercial</option>
              <option value="consultant">Consultant</option>
              <option value="analyste">Analyste</option>
              <option value="developpeur">Développeur</option>
              <option value="comptable">Comptable / Expert-comptable</option>
              <option value="rh-formation">RH / Formation</option>
              <option value="autre">Autre</option>
            </select>
            <input type="text" name="posteAutre" placeholder="Précisez votre poste..." 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent mt-2 hidden">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Secteur d'activité</label>
            <select name="secteurActivite" required 
                    class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus-border-accent">
              <option value="">Sélectionnez votre secteur</option>
              <option value="services-entreprises">Services aux entreprises</option>
              <option value="informatique-numerique">Informatique / Numérique</option>
              <option value="comptabilite-finance">Comptabilité / Finance</option>
              <option value="marketing-communication">Marketing / Communication</option>
              <option value="conseil-audit">Conseil / Audit</option>
              <option value="industrie">Industrie</option>
              <option value="commerce-distribution">Commerce / Distribution</option>
              <option value="sante">Santé</option>
              <option value="education-formation">Éducation / Formation</option>
              <option value="administration-publique">Administration publique</option>
              <option value="autre">Autre</option>
            </select>
            <input type="text" name="secteurAutre" placeholder="Précisez votre secteur d'activité..." 
                   class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent mt-2 hidden">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">Taille de l'entreprise</label>
            <select name="tailleEntreprise" required 
                    class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus-border-accent">
              <option value="startup">Startup (< 10 salariés)</option>
              <option value="tpe">TPE (10-19 salariés)</option>
              <option value="pme" selected>PME (20-249 salariés)</option>
              <option value="eti">ETI (250-4999 salariés)</option>
              <option value="grande-entreprise">Grande entreprise (5000+ salariés)</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Préférences de formation -->
      <div class="mb-8">
        <h3 class="text-lg font-semibold text-white border-b border-white/20 pb-2 mb-4">Niveau d'expérience</h3>
        <div>
          <label class="block text-sm font-medium text-white/90 mb-2">Votre niveau</label>
          <select name="niveauExperience" 
                  class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus-border-accent">
            <option value="debutant" selected>Débutant</option>
            <option value="intermediaire">Intermédiaire</option>
            <option value="avance">Avancé</option>
          </select>
        </div>
      </div>

      <!-- Besoins spécifiques -->
      <div class="mb-8">
        <h3 class="text-lg font-semibold text-white border-b border-white/20 pb-2 mb-4">
          Vos objectifs et besoins
        </h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">
              Quels sont vos principaux objectifs avec cette formation ?
            </label>
            <textarea name="objectifsPrincipaux" rows="3" required
                      class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent"
                      placeholder="Ex: Automatiser certaines tâches, améliorer l'efficacité, comprendre les enjeux..."></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">
              Questions spécifiques ou cas d'usage particuliers
            </label>
            <textarea name="questionsSpecifiques" rows="2"
                      class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus-border-accent"
                      placeholder="Optionnel : décrivez vos besoins spécifiques..."></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-white/90 mb-2">
              Comment avez-vous connu World of Numeric ?
            </label>
            <select name="commentConnaissance" 
                    class="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus-border-accent">
              <option value="site-web" selected>Site web</option>
              <option value="reseaux-sociaux">Réseaux sociaux</option>
              <option value="bouche-oreille">Bouche à oreille</option>
              <option value="partenaire">Par un partenaire</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Consentements RGPD -->
      <div class="mb-8 space-y-4">
        <h3 class="text-lg font-semibold text-white border-b border-white/20 pb-2">
          Consentements
        </h3>
        
        <div class="space-y-3">
          <label class="flex items-start space-x-3">
            <input type="checkbox" name="consentementRGPD" required
                   class="mt-1 w-4 h-4 text-[#3498db] border-white/20 rounded focus:ring-[#3498db] bg-white/5">
            <span class="text-sm text-white/90">
              J'accepte que mes données personnelles soient traitées par World of Numeric 
              pour le traitement de mon inscription et le suivi de ma formation. *
              <a href="/mentions-legales" class="text-[#3498db] underline">Voir nos mentions légales</a>
            </span>
          </label>
          
          <label class="flex items-start space-x-3">
            <input type="checkbox" name="consentementNewsletter"
                   class="mt-1 w-4 h-4 text-[#3498db] border-white/20 rounded focus:ring-[#3498db] bg-white/5">
            <span class="text-sm text-white/90">
              J'accepte de recevoir des informations sur les formations et actualités de World of Numeric 
              (vous pourrez vous désabonner à tout moment)
            </span>
          </label>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="flex flex-col sm:flex-row gap-4 justify-end pt-6 border-t border-white/10 mt-8">
        <button type="button" class="btn-secondary order-2 sm:order-1 px-6 py-3 rounded-lg border border-white/20 text-white bg-transparent hover:bg-white/10 hover:border-white/30 transition-all duration-200 font-medium">
          Annuler
        </button>
        <button type="submit" class="btn-primary order-1 sm:order-2 px-8 py-3 rounded-lg bg-[#ed4c67] text-white hover:bg-[#e74c3c] transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
          Envoyer ma demande d'inscription
        </button>
      </div>
    `;
    
    return form;
  }
  private setupEventListeners(): void {
    const form = this.element as HTMLFormElement;
    
    // Gestion de la soumission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSubmit();
    });
    
    // Gestion du bouton annuler
    const cancelBtn = form.querySelector('.btn-secondary');
    cancelBtn?.addEventListener('click', () => {
      this.close();
    });
    
    // Gestion du bouton de fermeture (croix)
    const closeBtn = form.querySelector('.btn-close');
    closeBtn?.addEventListener('click', () => {
      this.close();
    });
    
    // Validation en temps réel
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      input.addEventListener('input', () => {
        this.validateField(input as HTMLInputElement);
      });
    });
    
    // Gestion des champs "Autre" pour poste et secteur
    this.setupAutreFields();

    // Améliorer les selects (UI custom sombre/verre)
    this.enhanceSelects();

    // Gestion participants + total estimé
    const minusBtn = form.querySelector<HTMLButtonElement>('#personMinus');
    const plusBtn = form.querySelector<HTMLButtonElement>('#personPlus');
    const countInput = form.querySelector<HTMLInputElement>('#personCountInput');
    const totalSpan = form.querySelector<HTMLSpanElement>('#personTotal');

    const unitPrice = this.extractPriceValue(this.formData.formationPrice || '0');
    const updateTotal = () => {
      const count = Math.max(1, parseInt(countInput?.value || '1', 10));
      if (countInput) countInput.value = String(count);
      this.formData.personCount = count;
      if (totalSpan) totalSpan.textContent = `${(unitPrice * count).toFixed(2)}€`;
    };
    if (minusBtn) minusBtn.addEventListener('click', () => { if (countInput) { countInput.value = String(Math.max(1, (parseInt(countInput.value, 10) || 1) - 1)); updateTotal(); } });
    if (plusBtn) plusBtn.addEventListener('click', () => { if (countInput) { countInput.value = String((parseInt(countInput.value, 10) || 1) + 1); updateTotal(); } });
    if (countInput) countInput.addEventListener('input', updateTotal);
    updateTotal();
  }

  private extractPriceValue(price: string): number {
    if (!price) return 0;
    const match = price.match(/([0-9]+(?:[\.,][0-9]+)?)/);
    return match ? parseFloat(match[1].replace(',', '.')) : 0;
  }

  // Remplace les <select> natifs par une UI custom (dans la modale uniquement)
  private enhanceSelects(): void {
    const form = this.element as HTMLFormElement;
    const selects = Array.from(form.querySelectorAll<HTMLSelectElement>('select'));

    const closeAll = () => {
      form.querySelectorAll('.custom-select').forEach(el => {
        el.classList.remove('open');
        const opts = el.querySelector('.custom-options') as HTMLElement | null;
        if (opts) opts.classList.add('hidden');
      });
    };

    selects.forEach((select) => {
      if (select.dataset.enhanced === 'true') return;
      select.dataset.enhanced = 'true';

      // Wrapper
      const wrapper = document.createElement('div');
      wrapper.className = 'custom-select relative';

      // Trigger
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'custom-select-trigger w-full px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white text-left flex items-center justify-between';
      const label = document.createElement('span');
      label.textContent = select.options[select.selectedIndex]?.text || '—';
      const caret = document.createElement('span');
      caret.className = 'custom-select-caret';
      caret.innerHTML = '&#9662;';
      trigger.appendChild(label);
      trigger.appendChild(caret);

      // Options container
      const options = document.createElement('div');
      options.className = 'custom-options absolute left-0 right-0 mt-1 rounded-lg bg-[#111723] border border-white/15 shadow-2xl overflow-auto max-h-64 hidden';

      Array.from(select.options).forEach((opt) => {
        const optEl = document.createElement('div');
        optEl.className = 'custom-option px-4 py-2 text-white/90 hover:bg-white/10 cursor-pointer';
        if (opt.disabled) optEl.classList.add('opacity-60', 'cursor-not-allowed');
        optEl.textContent = opt.text;
        if (opt.selected) optEl.classList.add('active');

        optEl.addEventListener('click', () => {
          if (opt.disabled) return;
          // MAJ valeur select natif
          select.value = opt.value;
          label.textContent = opt.text;
          options.querySelectorAll('.custom-option.active').forEach(a => a.classList.remove('active'));
          optEl.classList.add('active');
          // Fermer
          wrapper.classList.remove('open');
          options.classList.add('hidden');
          // Propager l'événement change (pour champs "Autre")
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });

        options.appendChild(optEl);
      });

      // Toggle
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = wrapper.classList.contains('open');
        if (isOpen) {
          wrapper.classList.remove('open');
          options.classList.add('hidden');
        } else {
          closeAll();
          wrapper.classList.add('open');
          options.classList.remove('hidden');
        }
      });

      // Insérer dans le DOM
      select.classList.add('hidden-select');
      select.after(wrapper);
      wrapper.appendChild(trigger);
      wrapper.appendChild(options);
    });

    // Fermer au clic extérieur (persisant)
    const onDocClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      if (!this.element.contains(target)) {
        closeAll();
        return;
      }
      // Ferme tous les autres dropdowns quand on clique ailleurs dans la modale
      const current = target.closest('.custom-select');
      form.querySelectorAll('.custom-select.open').forEach(el => {
        if (el !== current) {
          el.classList.remove('open');
          const opts = el.querySelector('.custom-options') as HTMLElement | null;
          if (opts) opts.classList.add('hidden');
        }
      });
    };
    document.addEventListener('click', onDocClick);
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });
  }

  private setupAutreFields(): void {
    const form = this.element as HTMLFormElement;
    
    // Gestion du champ "Autre" pour le poste
    const posteSelect = form.querySelector('select[name="poste"]') as HTMLSelectElement;
    const posteAutreInput = form.querySelector('input[name="posteAutre"]') as HTMLInputElement;
    
    if (posteSelect && posteAutreInput) {
      posteSelect.addEventListener('change', () => {
        if (posteSelect.value === 'autre') {
          posteAutreInput.classList.remove('hidden');
          posteAutreInput.required = true;
        } else {
          posteAutreInput.classList.add('hidden');
          posteAutreInput.required = false;
          posteAutreInput.value = '';
        }
      });
    }
    
    // Gestion du champ "Autre" pour le secteur
    const secteurSelect = form.querySelector('select[name="secteurActivite"]') as HTMLSelectElement;
    const secteurAutreInput = form.querySelector('input[name="secteurAutre"]') as HTMLInputElement;
    
    if (secteurSelect && secteurAutreInput) {
      secteurSelect.addEventListener('change', () => {
        if (secteurSelect.value === 'autre') {
          secteurAutreInput.classList.remove('hidden');
          secteurAutreInput.required = true;
        } else {
          secteurAutreInput.classList.add('hidden');
          secteurAutreInput.required = false;
          secteurAutreInput.value = '';
        }
      });
    }
  }

  private validateField(field: HTMLInputElement): void {
    const isValid = field.checkValidity();
    
    if (isValid) {
      field.classList.remove('border-error');
      field.classList.add('border-neutral');
    } else {
      field.classList.remove('border-neutral');
      field.classList.add('border-error');
    }
  }

  private async handleSubmit(): Promise<void> {
    const form = this.element as HTMLFormElement;
    const formData = new FormData(form);
    
    // Mettre à jour les données
    Object.keys(this.formData).forEach(key => {
      const value = formData.get(key);
      if (value !== null) {
        (this.formData as any)[key] = key.includes('consentement') ? 
          formData.has(key) : value;
      }
    });
    
    // Gestion spéciale des champs "Autre"
    if (this.formData.poste === 'autre' && this.formData.posteAutre) {
      this.formData.poste = this.formData.posteAutre;
    }
    if (this.formData.secteurActivite === 'autre' && this.formData.secteurAutre) {
      this.formData.secteurActivite = this.formData.secteurAutre;
    }
    
    // Validation
    if (!this.validateForm()) {
      return;
    }
    
    try {
      // Afficher un loader
      this.showSubmitLoader();
      
      // Appeler le callback de soumission s'il existe
      if (this.submitCallback) {
        await this.submitCallback(this.formData);
      } else {
        // Comportement par défaut : redirection vers contact avec données
        await this.sendToContactForm();
      }
      
      this.showSuccessMessage();
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      this.showErrorMessage();
    } finally {
      this.hideSubmitLoader();
    }
  }

  private validateForm(): boolean {
    const form = this.element as HTMLFormElement;
    
    // Validation HTML5
    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }
    
    // Validation RGPD
    if (!this.formData.consentementRGPD) {
      alert('Vous devez accepter le traitement de vos données personnelles pour continuer.');
      return false;
    }
    
    return true;
  }

  private async sendToContactForm(): Promise<void> {
    // Préparer les données pour l'envoi
    const emailData = {
      to: 'contact@worldofnumeric.fr',
      subject: `Nouvelle inscription formation : ${this.formData.formationName}`,
      body: this.generateEmailBody()
    };
    
    // Simuler l'envoi (à remplacer par votre API)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Données d\'inscription:', this.formData);
    console.log('Email à envoyer:', emailData);
  }

  private generateEmailBody(): string {
    return `
      Nouvelle demande d'inscription à une formation
      
      === FORMATION ===
      Formation : ${this.formData.formationName}
      Prix : ${this.formData.formationPrice}
      
      === CONTACT ===
      Nom : ${this.formData.prenom} ${this.formData.nom}
      Email : ${this.formData.email}
      Téléphone : ${this.formData.telephone || 'Non renseigné'}
      Adresse : ${this.formData.adresse || 'Non renseignée'}
      Code postal : ${this.formData.codePostal || 'Non renseigné'}
      Ville : ${this.formData.ville || 'Non renseignée'}
      Pays : ${this.formData.pays || 'Non renseigné'}
      
      === ENTREPRISE ===
      Entreprise : ${this.formData.entreprise}
      SIREN : ${this.formData.siren || 'Non renseigné'}
      Poste : ${this.formData.poste}
      Secteur : ${this.formData.secteurActivite}
      Taille : ${this.formData.tailleEntreprise}
      Participants : ${this.formData.personCount || 1}
      
      === NIVEAU ===
      Niveau : ${this.formData.niveauExperience}
      
      === OBJECTIFS ===
      ${this.formData.objectifsPrincipaux}
      
      === QUESTIONS SPÉCIFIQUES ===
      ${this.formData.questionsSpecifiques || 'Aucune question spécifique'}
      
      === MARKETING ===
      Source de connaissance : ${this.formData.commentConnaissance}
      Newsletter : ${this.formData.consentementNewsletter ? 'Oui' : 'Non'}
    `;
  }

  private showSubmitLoader(): void {
    const submitBtn = this.element.querySelector('.btn-primary') as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `
      <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Envoi en cours...
    `;
  }

  private hideSubmitLoader(): void {
    const submitBtn = this.element.querySelector('.btn-primary') as HTMLButtonElement;
    submitBtn.disabled = false;
    submitBtn.innerHTML = 'Envoyer ma demande d\'inscription';
  }

  private showSuccessMessage(): void {
    // Créer et afficher un message de succès
    const successDiv = document.createElement('div');
    successDiv.className = 'fixed top-4 right-4 bg-[#2ecc71] text-white p-4 rounded-lg shadow-lg z-50';
    successDiv.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>Demande d'inscription envoyée avec succès !</span>
      </div>
    `;
    
    document.body.appendChild(successDiv);
    
    setTimeout(() => {
      successDiv.remove();
      this.close();
    }, 3000);
  }

  private showErrorMessage(): void {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'fixed top-4 right-4 bg-[#e74c3c] text-white p-4 rounded-lg shadow-lg z-50';
    errorDiv.innerHTML = `
      <div class="flex items-center space-x-2">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <span>Erreur lors de l'envoi. Veuillez réessayer.</span>
      </div>
    `;
    
    document.body.appendChild(errorDiv);
    
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  public setSubmitCallback(callback: (data: FormationInscriptionData) => Promise<void>): void {
    this.submitCallback = callback;
  }

  public getElement(): HTMLElement {
    return this.element;
  }

  public close(): void {
    this.element.remove();
  }

  public showModal(): void {
    // Sécurité: fermer toute ancienne modale de formation encore présente
    const existingOverlays = document.querySelectorAll('.formation-modal-overlay');
    if (existingOverlays.length) {
      existingOverlays.forEach(el => el.remove());
      document.body.style.overflow = '';
    }

    // Créer un overlay modal
    const overlay = document.createElement('div');
    overlay.className = 'formation-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl bg-[#0b1220]/60';
    overlay.style.display = 'flex';
    overlay.style.zIndex = '9999';
    
    // Utiliser le formulaire comme conteneur verre
    this.element.classList.add('w-full','max-w-5xl','max-h-[90vh]','overflow-y-auto');
    overlay.appendChild(this.element);
    document.body.appendChild(overlay);
    
    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden';
    
    // Fonction de fermeture
    const closeModal = () => {
      document.body.style.overflow = '';
      overlay.remove();
    };
    
    // Fermer au clic sur l'overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });
    
    // Fermer avec la touche Echap
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // Animation d'entrée
    overlay.style.opacity = '0';
    this.element.style.transform = 'scale(0.9) translateY(20px)';
    
    requestAnimationFrame(() => {
      overlay.style.transition = 'opacity 0.2s ease-out';
      this.element.style.transition = 'transform 0.2s ease-out';
      overlay.style.opacity = '1';
      this.element.style.transform = 'scale(1) translateY(0)';
    });
    
    // Modifier la méthode close pour fermer la modale
    const originalClose = this.close.bind(this);
    this.close = () => {
      overlay.remove();
      originalClose();
    };
  }
}

// Classe utilitaire pour gérer les inscriptions
import { isEnrollmentEnabled } from '../../utils/flags';

export class FormationInscriptionManager {
  public static createInscriptionModal(formationData: {
    id: string;
    name: string;
    price: string;
  }): FormationInscriptionForm {
    // Respect de la désactivation globale des inscriptions/paiements
    if (!isEnrollmentEnabled()) {
      if (typeof window !== 'undefined') {
        window.location.href = 'contact.html';
      }
    }

    const form = new FormationInscriptionForm({
      formationId: formationData.id,
      formationName: formationData.name,
      formationPrice: formationData.price
    });
    
    // Soumission: sauvegarde API puis redirection Stripe Checkout
    form.setSubmitCallback(async (data) => {
      // Sécurité: si désactivé, ne pas tenter d'appel API/Stripe
      if (!isEnrollmentEnabled()) {
        if (typeof window !== 'undefined') window.location.href = 'contact.html';
        return;
      }

      // 1) Sauvegarde côté API (inscription formation)
      const env: any = ((): any => {
        try { return (import.meta as any)?.env ?? {}; } catch { return {}; }
      })();
      const useProxy = String(env?.VITE_USE_API_PROXY || '').toLowerCase() === 'true'
        && String(env?.VITE_FORCE_API || '').toLowerCase() !== 'true';
      const apiBase = useProxy
        ? ''
        : (env?.VITE_API_BASE_URL
            ? `${env.VITE_API_BASE_URL}`
            : (process.env.NODE_ENV === 'development'
                ? 'http://localhost:3002'
                : 'https://worldofnumeric-api.herokuapp.com'));
      const saveRes = await fetch(`${apiBase}/api/formations/inscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!saveRes.ok) {
        const err = await saveRes.json().catch(() => ({}));
        throw new Error(err.error || `Erreur HTTP: ${saveRes.status}`);
      }
      const saved = await saveRes.json();
      console.log('✅ Inscription sauvegardée:', saved);

      // 2) Stripe Checkout avec métadonnées
      const { FormationStripeIntegration } = await import('../../services/FormationStripeIntegration');
      await FormationStripeIntegration.createCheckoutSession({
        formationId: formationData.id,
        formationName: formationData.name,
        formationPriceText: formationData.price,
        personCount: data.personCount || 1,
        details: data
      });

      return saved;
    });
    
    return form;
  }
}
